import React, { useState } from 'react';
import {
  useFloating,
  useHover,
  useInteractions,
  useTransitionStyles,
  flip,
  shift,
  offset,
} from '@floating-ui/react';
import { autoUpdate } from '@floating-ui/react-dom';

import Tooltip from './Tooltip';

const TooltipFloating = ({
  placement,
  text,
  ...restProps
}: { placement: any; text: string } & Record<string, any>) => {
  const [open, setOpen] = useState(false);

  const { x, y, strategy, refs, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: placement || 'top',
    middleware: [offset(6), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });
  const { isMounted, styles } = useTransitionStyles(context, {
    initial: {
      opacity: 0,
      transform: 'translateY(-10px)',
    },
    open: { opacity: 1, transform: 'translateY(-4px)' },
  });

  const hover = useHover(context, {
    move: false,
    delay: { open: 0, close: 175 },
    restMs: 20,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const styleProps = {
    position: strategy,
    top: y || 0,
    left: x || 0,
  };

  return (
    <Tooltip
      {...restProps}
      text={text}
      isMounted={isMounted}
      refs={refs}
      animationStyles={styles}
      styleProps={styleProps}
      getReferenceProps={getReferenceProps}
      getFloatingProps={getFloatingProps}
    />
  );
};

const TooltipContainer = ({
  placement,
  text,
  triggerElement,
  triggerClassName,
  ...restProps
}: {
  placement?: any;
  text?: string;
  triggerElement?: React.ReactNode;
  triggerClassName?: string;
} & Record<string, any>) => {
  if (!text) {
    return <>{triggerElement}</>;
  }

  return (
    <TooltipFloating
      placement={placement}
      text={text}
      triggerElement={triggerElement}
      triggerClassName={triggerClassName}
      {...restProps}
    />
  );
};

export default React.memo(TooltipContainer) as any;
